/**
 * PlatformFeaturePage — reusable SEO/marketing page shell.
 *
 * Every /platform/* page is data — we compose:
 *   - top nav (BrandMark + Platform / Directory / Pricing / Sign in)
 *   - navy PageHero with eyebrow, big title, subtitle, primary + secondary CTAs
 *   - "How it works" 3-step section
 *   - "What's inside" feature grid
 *   - "Who it's for" persona cards
 *   - "FAQs" accordion (native <details> for zero-dep + SEO friendliness)
 *   - "Related tools" cross-linking to sibling pages
 *   - closing CTA + footer
 *
 * Also sets <title>, meta description, keywords, og:* tags via
 * `useDocumentMeta`.  Fully server-renderable-friendly (pure JSX, no
 * effect-driven layout).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Check, ChevronRight, Menu, LogIn, UserPlus, Sparkles,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import BrandMark from '../../components/BrandMark';
import ThemeToggle from '../../components/ThemeToggle';
import PageHero from '../../components/PageHero';
import useDocumentMeta from '../../hooks/useDocumentMeta';

const PlatformFeaturePage = ({
  slug,
  eyebrow,
  title,
  titleAccent,
  subtitle,
  seo = {},
  primaryCta = { label: 'Get started free', to: '/login?mode=signup' },
  secondaryCta,
  highlights = [],       // [{ icon, label }]  — pills right under hero
  howItWorks = [],       // [{ step, title, body }]
  features = [],         // [{ icon, title, body }]
  personas = [],         // [{ icon, title, body }]
  faqs = [],             // [{ q, a }]
  related = [],          // [{ slug, title, blurb, icon }]
  finalCtaTitle = 'Ready to run better batches?',
  finalCtaBody = 'Sign up free — no credit card, no gate on the tools.',
}) => {
  const navigate = useNavigate();

  useDocumentMeta({
    title: seo.title || `${title} ${titleAccent || ''} — Freeze Dry Academy`.trim(),
    description: seo.description || subtitle,
    keywords: seo.keywords,
    ogType: 'article',
    canonical: seo.canonical,
  });

  return (
    <div className="min-h-screen bg-background" data-testid={`platform-page-${slug}`}>
      {/* ── Top nav ─────────────────────────────────────────── */}
      <header className="border-b border-border">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark size="md" subtitle="Platform" onClick={() => navigate('/')} />
          <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-foreground/80">
            <button onClick={() => navigate('/platform')} className="hover:text-foreground transition" data-testid="nav-platform">Platform</button>
            <button onClick={() => navigate('/directory')} className="hover:text-foreground transition">Directory</button>
            <button onClick={() => navigate('/pricing')} className="hover:text-foreground transition">Pricing</button>
            <button onClick={() => navigate('/consulting')} className="hover:text-foreground transition">Consulting</button>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/login')}
              className="rounded-full font-semibold hidden sm:inline-flex"
              data-testid="nav-signin"
            >
              <LogIn className="w-3.5 h-3.5 mr-1.5" /> Sign in
            </Button>
            <Button
              size="sm"
              onClick={() => navigate('/login?mode=signup')}
              className="rounded-full font-semibold"
              data-testid="nav-signup"
            >
              <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Sign up
            </Button>
          </div>
        </div>
      </header>

      {/* ── Hero ───────────────────────────────────────────── */}
      <PageHero
        eyebrow={eyebrow}
        title={<>{title} {titleAccent && <span className="text-amber-brand">{titleAccent}</span>}</>}
        description={subtitle}
        backTo="/platform"
        backLabel="All tools"
        testId={`hero-${slug}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="lg"
              onClick={() => navigate(primaryCta.to)}
              className="rounded-full font-extrabold px-6"
              data-testid={`hero-primary-cta-${slug}`}
            >
              {primaryCta.label} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            {secondaryCta && (
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate(secondaryCta.to)}
                className="rounded-full font-extrabold px-6 bg-background/10 border-background/30 text-background hover:bg-background/20 hover:text-background"
                data-testid={`hero-secondary-cta-${slug}`}
              >
                {secondaryCta.label}
              </Button>
            )}
          </div>
        }
      />

      {/* ── Highlights pills ───────────────────────────────── */}
      {highlights.length > 0 && (
        <section className="border-b border-border bg-muted/30" data-testid={`highlights-${slug}`}>
          <div className="container mx-auto px-6 py-6 flex flex-wrap items-center justify-center gap-2">
            {highlights.map(({ icon: Ic, label }, i) => (
              <span key={i} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 text-xs font-semibold">
                {Ic && <Ic className="w-3.5 h-3.5 text-primary" />} {label}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* ── How it works ───────────────────────────────────── */}
      {howItWorks.length > 0 && (
        <section className="container mx-auto px-6 py-20" data-testid={`how-it-works-${slug}`}>
          <div className="max-w-2xl mb-10">
            <span className="eyebrow">How it works</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">
              Simple, and built for daily use.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {howItWorks.map((s, i) => (
              <div key={i} className="pro-card p-6" data-testid={`step-${slug}-${i}`}>
                <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-heading font-extrabold">
                  {i + 1}
                </div>
                <h3 className="mt-4 font-heading text-lg font-bold tracking-tight">{s.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Features ───────────────────────────────────────── */}
      {features.length > 0 && (
        <section className="bg-muted/30 border-y border-border" data-testid={`features-${slug}`}>
          <div className="container mx-auto px-6 py-20">
            <div className="max-w-2xl mb-10">
              <span className="eyebrow">What&apos;s inside</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">
                Every part earns its keep.
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {features.map(({ icon: Ic, title: ft, body }, i) => (
                <div key={i} className="pro-card p-5" data-testid={`feature-${slug}-${i}`}>
                  <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    {Ic ? <Ic className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                  </div>
                  <h3 className="mt-4 font-heading text-base font-extrabold tracking-tight">{ft}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Personas ───────────────────────────────────────── */}
      {personas.length > 0 && (
        <section className="container mx-auto px-6 py-20" data-testid={`personas-${slug}`}>
          <div className="max-w-2xl mb-10">
            <span className="eyebrow">Who it&apos;s for</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">
              Built for real operators.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {personas.map(({ icon: Ic, title: pt, body }, i) => (
              <div key={i} className="pro-card p-5" data-testid={`persona-${slug}-${i}`}>
                <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  {Ic ? <Ic className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                </div>
                <h3 className="mt-4 font-heading text-base font-extrabold tracking-tight">{pt}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── FAQs ───────────────────────────────────────────── */}
      {faqs.length > 0 && (
        <section className="bg-muted/30 border-y border-border" data-testid={`faqs-${slug}`}>
          <div className="container mx-auto px-6 py-20 max-w-3xl">
            <span className="eyebrow">FAQs</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-2 mb-8">
              Common questions.
            </h2>
            <div className="space-y-3">
              {faqs.map(({ q, a }, i) => (
                <details
                  key={i}
                  className="pro-card p-4 group"
                  data-testid={`faq-${slug}-${i}`}
                >
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-3">
                    <span className="font-heading text-base font-extrabold tracking-tight">{q}</span>
                    <ChevronRight className="w-4 h-4 mt-1 flex-shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Related tools ──────────────────────────────────── */}
      {related.length > 0 && (
        <section className="container mx-auto px-6 py-20" data-testid={`related-${slug}`}>
          <div className="max-w-2xl mb-10">
            <span className="eyebrow">Related tools</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">
              Pair it with the rest of the platform.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {related.map(({ slug: rslug, title: rt, blurb, icon: Ic }, i) => (
              <button
                key={i}
                onClick={() => navigate(`/platform/${rslug}`)}
                className="pro-card-interactive text-left p-5 group"
                data-testid={`related-${slug}-${rslug}`}
              >
                <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  {Ic ? <Ic className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                </div>
                <h3 className="mt-4 font-heading text-base font-extrabold tracking-tight flex items-center gap-1">
                  {rt}
                  <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition" />
                </h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{blurb}</p>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ── Final CTA ──────────────────────────────────────── */}
      <section className="bg-navy-spotlight text-background" data-testid={`final-cta-${slug}`}>
        <div className="container mx-auto px-6 py-20 text-center max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-background/20 bg-background/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-background/70">
            <Sparkles className="w-3 h-3" /> Freeze Dry Academy
          </div>
          <h2 className="mt-5 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">
            {finalCtaTitle}
          </h2>
          <p className="mt-4 text-background/70 text-base sm:text-lg leading-relaxed">
            {finalCtaBody}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              size="lg"
              onClick={() => navigate(primaryCta.to)}
              className="rounded-full font-extrabold px-6 bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid={`final-primary-cta-${slug}`}
            >
              {primaryCta.label} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/platform')}
              className="rounded-full font-extrabold px-6 bg-background/10 border-background/30 text-background hover:bg-background/20 hover:text-background"
              data-testid={`final-secondary-cta-${slug}`}
            >
              See all tools
            </Button>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────── */}
      <footer className="border-t border-border bg-background">
        <div className="container mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            <BrandMark size="sm" onClick={() => navigate('/')} />
            <span>© {new Date().getFullYear()} Freeze Dry Academy · Peak and Prairie Industries Corp.</span>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <button onClick={() => navigate('/platform')} className="hover:text-foreground transition">Platform</button>
            <button onClick={() => navigate('/pricing')} className="hover:text-foreground transition">Pricing</button>
            <button onClick={() => navigate('/directory')} className="hover:text-foreground transition">Directory</button>
            <button onClick={() => navigate('/consulting')} className="hover:text-foreground transition">Consulting</button>
            <button onClick={() => navigate('/login')} className="hover:text-foreground transition">Sign in</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PlatformFeaturePage;
